import { H1, H2, Hidden } from '@ahmdigital/ui';
import { isEmpty, isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  BorderBox,
  DesktopVideoWrapper,
  HeroContainer,
  ImageWrapper,
  OfferSectionWrapper,
  StyledSection,
  WidgetOfferSectionDesktopWrapper,
  WidgetOfferSectionMobileWrapper,
  WidgetWrapper,
} from './styles';
import { getHomeVideoSrc } from '../../../../ahm-constants/ui';
import device from '../../../../utils/device';
import HealthWidget from '../health-widget';
import ResponsiveVideo from '../../../../components/responsive-video';
import selectReferralCode from '../../../../selectors/select-referral-code';
import useIsAfterHydration from '../../../../hooks/use-is-after-hydration';
import useShouldShowOfferBanner from '../../../../components/offers/banner/use-should-show-offer-banner';
import useViewportValue from '../../../../utils/use-viewport-value';
import type { HeroSectionProps } from '../../types';

const HeroSection = ({ heroOfferSection, name, subtitle }: HeroSectionProps) => {
  const isAfterHydration = useIsAfterHydration();
  const activeOffers = heroOfferSection?.props?.activeOffers;
  const referralCode = useSelector(selectReferralCode);
  const shouldShow = useShouldShowOfferBanner({ activeOffers });
  const isOfferSectionHidden = !shouldShow || !isNil(referralCode);
  const isTabletOrMobileBreakpointWithViewport = useViewportValue(device.isTabletOrMobile);
  const headingLooksLike = isTabletOrMobileBreakpointWithViewport ? 'h2' : undefined;

  const homeVideoSrc = useMemo(() => getHomeVideoSrc(isAfterHydration), [isAfterHydration]);

  return (
    <BorderBox>
      <HeroContainer>
        <StyledSection>
          <H1 looksLike={headingLooksLike}>{name}</H1>
          {!isEmpty(subtitle) && (
            <Hidden xs sm md>
              <H2 looksLike="h6" variant={['regular', 'paddingBottomSpacer10']}>
                {subtitle}
              </H2>
            </Hidden>
          )}
        </StyledSection>

        <WidgetOfferSectionMobileWrapper>
          <WidgetWrapper>
            <HealthWidget variant="hero" />
          </WidgetWrapper>
          <OfferSectionWrapper $hasOffer={!isOfferSectionHidden}>{heroOfferSection}</OfferSectionWrapper>
        </WidgetOfferSectionMobileWrapper>

        <WidgetOfferSectionDesktopWrapper>
          <OfferSectionWrapper $hasOffer={!isOfferSectionHidden}>{heroOfferSection}</OfferSectionWrapper>
          <WidgetWrapper>
            <HealthWidget variant="hero" />
          </WidgetWrapper>
        </WidgetOfferSectionDesktopWrapper>

        <ImageWrapper>
          <DesktopVideoWrapper $extraTopMargin={!isOfferSectionHidden} $isOfferSectionHidden={isOfferSectionHidden}>
            <ResponsiveVideo autoPlay muted playsInline renderOn={['lg']}>
              <source src={homeVideoSrc} type="video/mp4" />
            </ResponsiveVideo>
          </DesktopVideoWrapper>
        </ImageWrapper>
      </HeroContainer>
    </BorderBox>
  );
};

export default HeroSection;
